import { environment } from '@environment';
import { AbstractAuthService } from 'core/models/auth-service';
import { ConnectLoginRequest, ConnectRefreshResponse, ConnectRequest, ConnectStoreBase, ConnectStoreLoginResponse, UserAuth } from 'core/stores/auth/auth-types';
import { AuthUtils, ConnectAuthType } from 'core/stores/auth/auth-utils';
import { SiteConfigStore } from 'core/stores/site-config.store';
import { Util } from 'utils/common.utils';
import { EndpointHelper } from './endpoint.helper';
import { HttpClient } from './http';

export class AuthConnectService implements AbstractAuthService {
  hasRefresh = true;

  readonly redirectBuilder = new EndpointHelper(`${environment.CONNECT_URL}`);
  readonly apiBuilder = new EndpointHelper(`${environment.CONNECT_PROXY_URL}`);

  login(user: UserAuth): Promise<UserAuth> {
    if (user.auth.authCode) {
      const url = this.apiBuilder.createUrl('verify')
        .build();

      return HttpClient.GetInstance.post<any, ConnectLoginRequest>(url, { authCode: user.auth.authCode })
        .then(
          (res) => {
            if (!res?.data) {
              return Promise.reject('Failed to verify code');
            }

            return this.signIn(res.data);
          }
        );
    }

    const userAsLoginDto = AuthUtils.MapUserToConnectLoginDTO(user);

    if (!userAsLoginDto || !userAsLoginDto.accessToken) {
      return Promise.reject('Unable to sign-in- no connect customer found');
    }

    return this.signIn(userAsLoginDto)
  }

  signIn(userAsLoginDto: ConnectLoginRequest) {
    const endpoint = this.apiBuilder.createUrl('signin');

    if (!!userAsLoginDto.forceReAuth) {
      endpoint.addQueryParam("refresh", "true");
    }

    const url = endpoint.build();

    return HttpClient.GetInstance.post<ConnectLoginRequest, string | ConnectStoreBase>(url, userAsLoginDto)
      .then(
        (res) => {
          const connectStore = AuthUtils.ParseResponseData<ConnectStoreLoginResponse | ConnectStoreBase>(res?.data);

          // NOTE: this is a legacy item & needs to be removed from all react apps
          AuthUtils.ConnectStore().set(connectStore);

          return AuthUtils.MapConnectUserToUser(ConnectAuthType.LegacyConnectStore, connectStore) as UserAuth;
        }
      );
  }

  refreshToken(user: UserAuth): Promise<UserAuth> {
    const userAsRefreshDto = AuthUtils.MapUserToConnectRefreshDTO(user);

    if (!userAsRefreshDto || !userAsRefreshDto.accessToken) {
      return Promise.reject('Unable to refresh session- no connect customer found');
    }

    return HttpClient.GetInstance.post<ConnectRequest, string | ConnectRefreshResponse>(`${environment.CONNECT_PROXY_URL}/refresh`, userAsRefreshDto)
      .then((res) => {
        const refreshResponse = AuthUtils.ParseResponseData<ConnectRefreshResponse>(res?.data);
        const refreshedUser = AuthUtils.MapConnectRefreshResponse(refreshResponse);

        if (!refreshedUser?.auth?.accessToken) {
          return Promise.reject('Unable to refresh session');
        }

        return { ...user, ...refreshedUser };
      });
  }

  signInUrl(path?: string, locale?: string): void {
    const redirectURL = Util.GetRedirectUrl(path);
    const connectClientId = SiteConfigStore.connectClientId();

    const url = this.redirectBuilder.createUrl('signin')
      .addQueryParamUrl('redirect_uri', redirectURL)
      .addQueryParam('client_id', connectClientId)
      .addQueryParam('bypasslinking', false)
      .addQueryParam('show_bc', false)
      .addQueryParam('lang', locale)
      .addQueryParam('usejwt', `${environment.USE_CONNECT_JWT}`)
      .build()

    window.location.assign(url);
  }

  registerUrl(path?: string, locale?: string): void {
    const redirectURL = Util.GetRedirectUrl(path);
    const connectClientId = SiteConfigStore.connectClientId();

    const url = this.redirectBuilder.createUrl('signup')
      .addQueryParamUrl('redirect_uri', redirectURL)
      .addQueryParam('client_id', connectClientId)
      .addQueryParam('bypasslinking', false)
      .addQueryParam('lang', locale)
      .addQueryParam('usejwt', `${environment.USE_CONNECT_JWT}`)
      .build();

    window.location.assign(url);
  }

  logout() {
    // TODO: handle logout
  }
}